import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Calculator, Loader2 } from "lucide-react";
import { adminGetAiCreditRates, adminUpdateAiCreditRates } from "../../../lib/api";
import { PROVIDER_LABELS } from "./constants";

export function AiCreditRatesPanel() {
    const [rates, setRates] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminGetAiCreditRates();
            setRates(r);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load rates.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const setRate = (k, v) => setRates((s) => ({ ...s, [k]: v }));

    const save = async () => {
        const clean = {};
        for (const [k, v] of Object.entries(rates)) {
            const n = Number(v);
            if (Number.isFinite(n) && n >= 0) clean[k] = n;
        }
        setSaving(true);
        try {
            const r = await adminUpdateAiCreditRates(clean);
            setRates(r);
            toast.success("AI rates saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save rates.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            className="bg-[#131A2F]/80 border border-[#2A3655] rounded-md overflow-hidden"
            data-testid="ai-credit-rates-panel"
        >
            <header className="flex items-center justify-between px-5 py-3 border-b border-[#2A3655] bg-[#1C253C]">
                <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-[#00A3FF]" />
                    AI credit costs per call
                </h2>
                <button
                    type="button"
                    onClick={save}
                    disabled={loading || saving}
                    data-testid="ai-rates-save"
                    className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white disabled:opacity-50"
                >
                    {saving ? (
                        <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                    ) : (
                        <Save className="w-3 h-3 inline mr-1" />
                    )}
                    Save rates
                </button>
            </header>
            <div className="p-5">
                <p className="text-xs text-slate-400 mb-3">
                    How many credits each AI <span className="text-white font-semibold">call</span> deducts
                    from a user's monthly balance. Set per-provider so heavier
                    models (Claude) cost more than light ones (Gemini). Credits
                    are debited on every call — including AI Takeoff iterations
                    and Analyze passes — so complex jobs naturally use more.
                </p>
                {loading ? (
                    <div className="text-sm text-slate-500">Loading…</div>
                ) : (
                    <div className="space-y-2">
                        {Object.entries(rates).map(([k, v]) => (
                            <div
                                key={k}
                                className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm"
                                data-testid={`ai-rate-row-${k}`}
                            >
                                <div className="col-span-7">
                                    <div className="text-sm font-semibold text-white">
                                        {PROVIDER_LABELS[k] || k}
                                    </div>
                                    <div className="text-[10px] text-slate-500 font-mono">
                                        {k}
                                    </div>
                                </div>
                                <div className="col-span-5 flex items-center gap-1.5 justify-end">
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.5"
                                        value={v}
                                        onChange={(e) =>
                                            setRate(k, Number(e.target.value))
                                        }
                                        data-testid={`ai-rate-input-${k}`}
                                        className="w-24 bg-[#131A2F] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm px-2 py-1 text-sm text-right tabular-nums text-white outline-none"
                                    />
                                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold whitespace-nowrap">
                                        credits / call
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

// ----- Profit calculator -----
